/**
 * What each consent permits — pure, because these are decisions with legal
 * consequences and they should be answerable without rendering anything.
 *
 * 🔴 THREE CONSENTS, THREE LEGAL BASES (ADR-016, privacy-compliance.md §4).
 * Collapsing them *"means either revoking messaging silently destroys a child's
 * learning history, or — worse — data is retained on the strength of a
 * marketing opt-in."*
 */

export interface ConsentChoices {
  /** On-device inference. Declining means the tap/seated path. */
  camera: boolean
  /** `ParentalConsent` — may we retain this child's gameplay data? */
  retain: boolean
  /** `whatsappOptIn` on the User — may we message this parent? */
  whatsapp: boolean
}

/**
 * How the consenting adult's status as parent/guardian was established.
 *
 * 🔴 THIS FIELD IS WHAT MAKES CONSENT VERIFIABLE, and its absence is why §7
 * does not write a `ParentalConsent` row. privacy-compliance.md §4: *"DPDP does
 * not accept 'a checkbox was ticked.' The record must show who consented, HOW
 * their status as parent/guardian was established, and when."*
 */
export type VerificationMethod =
  /** The parent proved control of the phone the roster recorded. Unit 9 / P3. */
  | 'phone-otp'
  /** A school administrator attests, on paper. Not yet a flow. */
  | 'school-attested'

export interface ConsentIntent {
  choices: ConsentChoices
  /** Null until something actually verified who this adult is. */
  verifiedBy: VerificationMethod | null
  /** When the adult made these choices. */
  at: number
}

export const noConsent = (): ConsentChoices => ({
  camera: false,
  retain: false,
  whatsapp: false,
})

/**
 * 🔴 A CHILD MAY ALWAYS PLAY. There is no combination of declines that stops
 * them.
 *
 * The rule: *"Declining retention does not block play — it stops data being
 * kept"*, and the consent path already implements exactly that (no consent row →
 * `recorded: false` → **zero events written**). Declining the camera routes to
 * the tap twin, which I3 makes an *equal* path rather than a lesser one.
 *
 * A gate that blocked play would also make declining feel like a punishment,
 * which is how a parent ends up ticking a box they do not mean.
 */
export const mayPlay = (): true => true

/** Which input path the child gets. Both are complete games (I3). */
export function inputPath(choices: ConsentChoices): 'camera' | 'tap' {
  return choices.camera ? 'camera' : 'tap'
}

/**
 * 🔴 MAY ANYTHING BE RETAINED? The legal floor, and the question §4.3's
 * precedence chain asks first.
 *
 * Retention needs **both** the choice and a verification of who made it. An
 * unverified tick is not consent under DPDP — so this returns false, and the
 * child plays with nothing kept, which is the same state as declining.
 */
export function mayRetain(intent: ConsentIntent): boolean {
  return intent.choices.retain && intent.verifiedBy !== null
}

/**
 * Whether this intent could be written as a `ParentalConsent` row at all.
 *
 * 🔴 THE REASON UNIT 7 WRITES NOTHING. A5 runs on a **shared classroom
 * laptop**: a checkbox ticked there proves nothing about who ticked it — the
 * child, a teacher, or another child's parent. Writing a row anyway would
 * create a record that *looks* like compliance and is not, and because §4.3
 * reads that row as the floor, it would **turn retention on for a child whose
 * parent may never have been asked**.
 *
 * A false green is more dangerous than a red. See OD-50.
 */
export function isVerifiable(intent: ConsentIntent): boolean {
  return intent.verifiedBy !== null
}

/**
 * Whether the adult has actually decided, so A5 can be *"a hard blocker in the
 * flow, not a dismissible screen"*.
 *
 * ⚠️ Deciding is not agreeing: an adult who declines all three has decided, and
 * the child plays. What A5 must not accept is being skipped.
 */
export function hasDecided(touched: Partial<Record<keyof ConsentChoices, boolean>>): boolean {
  return (
    touched.camera === true && touched.retain === true && touched.whatsapp === true
  )
}

/** The messaging opt-in, which touches nothing else (ADR-016). */
export const mayMessageParent = (choices: ConsentChoices): boolean => choices.whatsapp
